import { CHAIN, TonProofItemReplySuccess } from "@tonconnect/sdk";
import { useTonConnectUI, useTonWallet } from "@tonconnect/ui-react";
import { Address, Sender, SenderArguments } from "@ton/core";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { backend } from "@/services/backend";
import useInterval from "./useInterval";
import { LSService } from "@/services/storage";
import { TonClient } from "@ton/ton";
import { fromNano } from "@ton/core";
import { defaultEndpoint } from "@/config";
import { sleep } from "../utils/common";
import { sendGtag } from "@/utils/stats";
import { useDispatch, useSelector } from "react-redux";
import { actions, RootState } from "@/state/reducer";

// Global state to prevent multiple concurrent balance fetches across all hook instances
let globalBalanceFetchInProgress = false;

export function useTonConnect(): {
  sender: Sender;
  connected: boolean;
  wallet: string | null;
  network: CHAIN | null;
  balance: string | null;
  fetchBalance: () => Promise<string | null>;
  recreatePayloadFunction: () => Promise<void>;
} {
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const firstProofLoading = useRef<boolean>(true);
  const [authorized, setAuthorized] = useState(false);

  const isBalanceRequestPending = useRef<boolean>(false);
  const balance = useSelector((state: RootState) => state.balance);
  const balanceAge = useSelector((state: RootState) => state.balanceAge);
  const dispatch = useDispatch();

  const walletAddress = wallet?.account?.address ?? null;
  const authInProgressRef = useRef(false);
  const prevAddressRef = useRef<string | null>(null);

  const initAuthForAddress = useCallback(
    async (address: string) => {
      // 🛑 защита от повторного параллельного вызова
      if (authInProgressRef.current) {
        console.log("⏳ Auth already in progress, skipping");
        return;
      }
      authInProgressRef.current = true;

      try {
        console.log("🔐 Init auth for address:", address);

        // 1️⃣ Проверяем сохранённый токен
        const savedToken = await LSService.getTonAuthToken();
        if (savedToken) {
          const valid = await backend.checkToken(savedToken);
          if (valid) {
            console.log("✅ Using saved auth token");
            setAuthorized(true);
            return;
          }

          console.warn("❌ Saved token invalid, clearing");
          // LSService.deleteTonAuthToken();
        }

        // 2️⃣ Ждём tonProof (увеличено время ожидания для iOS/mobile)
        let tonProof: any = null;

        // Увеличено до 10 попыток × 500ms = 5 секунд для iOS
        for (let i = 0; i < 10; i++) {
          const proof = tonConnectUI.wallet?.connectItems?.tonProof ?? null;

          if (proof && "proof" in proof) {
            tonProof = proof;
            console.log(`✅ tonProof received after ${(i + 1) * 500}ms`);
            break;
          }

          await sleep(500);
        }

        if (!tonProof) {
          console.error("❌ tonProof not available after 5 seconds");
          console.warn(
            "⚠️ Please reconnect your wallet to enable all features"
          );
          setAuthorized(false);
          return;
        }

        if (!tonConnectUI?.wallet?.account) {
          console.error("❌ Wallet account not available");
          return;
        }

        // 3️⃣ Проверяем proof на бэкенде
        const token = await backend.checkWalletRights(
          tonProof.proof,
          tonConnectUI?.wallet?.account
        );

        if (token) {
          LSService.setTonAuthToken(token);
          LSService.updateAuthVersion();
          setAuthorized(true);
          console.log("✅ Auth token created and saved");
        } else {
          console.error("❌ Backend did not return auth token");
          console.warn(
            "⚠️ Authentication failed. Please reconnect your wallet."
          );
          setAuthorized(false);
        }
      } catch (err) {
        console.error("❌ initAuthForAddress failed:", err);
        setAuthorized(false);
      } finally {
        authInProgressRef.current = false;
      }
    },
    [tonConnectUI]
  );

  useEffect(() => {
    const prev = prevAddressRef.current;

    if (prev === walletAddress) return;

    prevAddressRef.current = walletAddress;

    // 🔌 отключение
    if (!walletAddress && prev) {
      setAuthorized(false);
      // LSService.deleteTonAuthToken();
      return;
    }

    // 🔐 первое подключение
    if (walletAddress && !prev) {
      initAuthForAddress(walletAddress);
      return;
    }

    // 🔁 смена адреса
    if (walletAddress && prev && walletAddress !== prev) {
      setAuthorized(false);
      // LSService.deleteTonAuthToken();
      initAuthForAddress(walletAddress);
    }
  }, [walletAddress]);

  // Removed duplicate onStatusChange handler - using the one at the end of the hook instead

  const recreatePayloadFunction = async () => {
    if (firstProofLoading.current) {
      tonConnectUI.setConnectRequestParameters({ state: "loading" });
      firstProofLoading.current = false;
    }

    try {
      const payload = await backend.generatePayload();
      // console.log("Generated payload:", payload);
      if (payload) {
        tonConnectUI.setConnectRequestParameters({
          state: "ready",
          value: payload,
        });
        console.log("✅ Payload generated successfully");
      } else {
        console.error(
          "❌ Backend payload generation failed - auth may not work properly"
        );
        // Still allow connection attempt, but warn user
        tonConnectUI.setConnectRequestParameters(null);
      }
    } catch (error) {
      console.error("❌ Failed to generate payload:", error);
      // Still allow connection attempt
      tonConnectUI.setConnectRequestParameters(null);
    }
  };

  const recreateProofPayload = useCallback(recreatePayloadFunction, [
    tonConnectUI,
    firstProofLoading,
  ]);

  if (firstProofLoading.current) {
    recreateProofPayload();
  }

  useInterval(recreateProofPayload, 1200000);

  useEffect(() => {
    recreateProofPayload();
  }, [tonConnectUI]);

  const handleWalletStatus = async (w: any) => {
    if (!w) {
      setAuthorized(false);
      return;
    }

    sendGtag("wallet_connect", {
      wallet: w.account.address,
    });

    // Увеличено для iOS/mobile: 10 попыток × 500ms = 5 секунд
    let retryCount = 0;
    while (!w.connectItems && retryCount < 10) {
      console.log(
        `⏳ Waiting for connectItems... attempt ${retryCount + 1}/10`
      );
      await new Promise(resolve => setTimeout(resolve, 500));
      retryCount++;
    }

    if (!w.connectItems) {
      console.error("❌ Connect items not available after 5 seconds");
      // Проверяем, есть ли сохранённый токен
      const accessToken = await LSService.getTonAuthToken();
      if (accessToken) {
        console.log("🔍 Checking saved token...");
        const checkResult = await backend.checkToken(accessToken);
        if (checkResult) {
          console.log("✅ Saved token is valid");
          setAuthorized(true);
          return;
        } else {
          console.warn("❌ Saved token invalid, clearing");
          // LSService.deleteTonAuthToken();
          // setAuthorized(false);
        }
      } else {
        console.warn("⚠️ No connectItems and no saved token - auth incomplete");
        // setAuthorized(false);
      }
      return;
    }

    const tonProof = w.connectItems.tonProof;

    if (tonProof) {
      try {
        console.log("🔐 Verifying tonProof with backend...");
        // Отправляем proof на бэкенд для проверки
        const token = await backend.checkWalletRights(
          tonProof.proof,
          w.account
        );
        if (token) {
          backend.checkCookieAuth().then((res) => {
            console.log(res)
          }).catch(console.error)
          // Сохраняем токен для последующего использования
          await LSService.setTonAuthToken(token);
          await LSService.updateAuthVersion();
          setAuthorized(true);
          console.log("✅ Authorized with backend, token saved");
        } else {
          console.error("❌ Backend did not return auth token");
          setAuthorized(false);
        }
      } catch (err) {
        console.error("❌ Failed to verify tonProof:", err);
        setAuthorized(false);
      }
    } else {
      console.warn("⚠️ No tonProof in connectItems - checking saved token");
      const accessToken = await LSService.getTonAuthToken();
      if (accessToken) {
        const checkResult = await backend.checkToken(accessToken);
        if (checkResult) {
          console.log("✅ Using saved token");
          setAuthorized(true);
        } else {
          console.warn("❌ Saved token invalid");
          setAuthorized(false);
        }
      }
    }
  };

  const fetchBalance = useCallback(async () => {
    if (!wallet?.account.address) {
      return null;
    }

    if (globalBalanceFetchInProgress || isBalanceRequestPending.current) {
      return balance;
    }

    const date = Date.now();
    if (balanceAge && balanceAge > 0) {
      if (date - balanceAge < 10000) {
        return balance;
      }
    }

    globalBalanceFetchInProgress = true;
    isBalanceRequestPending.current = true;

    try {
      const client = new TonClient({
        endpoint: defaultEndpoint,
      });
      await sleep(500);
      const balanceResult = await client.getBalance(
        Address.parse(wallet.account.address)
      );
      const balanceStr = fromNano(balanceResult).toString();
      await sleep(500);
      dispatch(actions.setBalance(balanceStr));
      dispatch(actions.setBalanceAge(date));
      return balanceStr;
    } catch (error: any) {
      console.error("Error fetching balance:", error);
      return balance;
    } finally {
      globalBalanceFetchInProgress = false;
      isBalanceRequestPending.current = false;
    }
  }, [wallet?.account.address, balance, balanceAge, dispatch]);

  useEffect(() => {
    if (wallet?.account.address) {
      fetchBalance();
    }
  }, [wallet?.account.address, fetchBalance]);

  useEffect(() => {
    const unsubscribe = tonConnectUI.onStatusChange(async w => {
      handleWalletStatus(w);
    });

    if (tonConnectUI.wallet) {
      handleWalletStatus(tonConnectUI.wallet);
    }

    return () => {
      unsubscribe();
    };
  }, [tonConnectUI]);

  /* useEffect(
    () => tonConnectUI.onStatusChange(handleWalletStatus),
    [tonConnectUI]
  ); */

  return {
    sender: {
      send: async (args: SenderArguments) => {
        // Check if wallet is connected before attempting to send transaction
        if (!wallet?.account.address) {
          throw new Error("Wallet is not connected");
        }

        try {
          await tonConnectUI.sendTransaction({
            messages: [
              {
                address: args.to.toString(),
                amount: args.value.toString(),
                payload: args.body?.toBoc().toString("base64"),
              },
            ],
            validUntil: Date.now() + 300000,
          });
          return; // Success, exit the retry loop
        } catch (error: any) {
          console.error(`Transaction failed:`, error);

          // Check if this is a user cancellation
          if (
            error?.message?.includes("cancelled") ||
            error?.message?.includes("rejected") ||
            error?.message?.includes("denied") ||
            error?.message?.includes("User rejected") ||
            error?.name === "UserRejectsError" ||
            error?.code === 4001 ||
            error?.code === 300
          ) {
            // Re-throw cancellation errors so they can be handled upstream
            throw new Error("Transaction was cancelled by user");
          }

          // Re-throw all other errors as well
          throw error;
        }
      },
      address: wallet?.account.address
        ? Address.parse(wallet?.account.address as string)
        : undefined,
    },
    connected: !!wallet?.account.address,
    wallet: wallet?.account.address ?? null,
    network: wallet?.account.chain ?? null,
    balance,
    recreatePayloadFunction,
    fetchBalance,
  };
}
